/**
 * EPC Form Wizard Types
 * Types for the multi-step EPC form wizard
 *
 * @version 2025.2.0
 */

import { Epc, EpcFormData } from '../../../../types/epc';

/**
 * Wizard step identifiers
 */
export type EPCWizardStep = 'identification' | 'technical' | 'stock';

/**
 * Step configuration
 */
export interface StepConfig {
  key: EPCWizardStep;
  label: string;
  optional: boolean;
}

/**
 * All wizard steps configuration
 */
export const EPC_WIZARD_STEPS: StepConfig[] = [
  { key: 'identification', label: 'Identificação', optional: false },
  { key: 'technical', label: 'Técnico', optional: true },
  { key: 'stock', label: 'Estoque', optional: true },
];

/**
 * Props for EPCFormWizard component
 */
export interface EPCFormWizardProps {
  /** Initial data for editing */
  initialData?: Partial<Epc>;
  /** Callback when form is submitted */
  onSubmit: (data: EpcFormData) => Promise<void>;
  /** Callback when wizard is cancelled */
  onCancel: () => void;
  /** Whether form is submitting */
  isSubmitting?: boolean;
}

/**
 * Wizard internal state
 */
export interface EPCWizardState {
  /** Current step index */
  activeStep: number;
  /** Form data collected across steps */
  formData: Partial<EpcFormData>;
  /** Loading state during validation */
  loading: boolean;
  /** Error message if operation failed */
  error: string | null;
}

/**
 * Props for step components
 */
export interface StepProps {
  disabled?: boolean;
}

/**
 * Props for IdentificationStep
 */
export interface IdentificationStepProps extends StepProps {}

/**
 * Props for TechnicalStep
 */
export interface TechnicalStepProps extends StepProps {}

/**
 * Props for StockStep
 */
export interface StockStepProps extends StepProps {}

/**
 * Return type for useEPCFormWizard hook
 */
export interface UseEPCFormWizardReturn {
  // State
  state: EPCWizardState;
  steps: StepConfig[];

  // Step navigation
  isFirstStep: boolean;
  isLastStep: boolean;
  currentStepKey: EPCWizardStep;
  canSkip: boolean;

  // Actions
  handleNext: () => Promise<boolean>;
  handleBack: () => void;
  handleSkip: () => void;
  setError: (error: string | null) => void;
  reset: () => void;
}
